import * as React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import { formatCurrency } from "../../../utils/utils";
import { formatDate } from "./InvoiceUtils";
import { InvoiceStatus } from "../../../utils/constants";
import {
  describePaymentMethod,
  getBalance,
  sumPayments,
} from "./invoicePaymentUtils";

const useStyles = makeStyles({
  text: {
    "@media print": {
      color: "#000",
    },
  },
});

const ServiceRow = ({ item }) => {
  const classes = useStyles();

  const qty = item.quantity ?? null;
  const units = item.units ?? "";
  const rate = item.unitCost ?? null;

  const amount = Number(item.amount ?? 0);
  const tax = Number(item.itemTax ?? 0);
  const lineTotal = amount + tax;

  return (
    <TableRow key={item.id || item.serviceId}>
      {/* Description + optional note */}
      <TableCell
        colSpan={2}
        style={{ textAlign: "left" }}
        className={classes.text}
      >
        {item.service?.name || ""}
        {/* Qty and Rate already show the quantity. Repeated beside a note it
            contradicts a unit-day storage line: "106 CAR" over "11 CAR,
            106 CAR-days". */}
        {qty && units && !item.serviceNote ? ` : ${qty} ${units}` : ``}
        {item.serviceNote ? (
          <>
            <br />
            <strong>Note:</strong> {item.serviceNote}
          </>
        ) : null}
      </TableCell>

      {/* Qty */}
      <TableCell style={{ textAlign: "right" }} className={classes.text}>
        {qty != null ? qty : ""}
      </TableCell>

      {/* Rate */}
      <TableCell style={{ textAlign: "right" }} className={classes.text}>
        {rate != null
          ? `${formatCurrency(rate)}${units ? ` / ${units}` : ""}`
          : ""}
      </TableCell>

      {/* Service Date */}
      <TableCell style={{ textAlign: "left" }} className={classes.text}>
        {formatDate(item.tempServiceDate || item.serviceDate)}
      </TableCell>

      {/* Amount, Tax, Line Total */}
      <TableCell style={{ textAlign: "right" }} className={classes.text}>
        {formatCurrency(amount)}
      </TableCell>
      <TableCell style={{ textAlign: "right" }} className={classes.text}>
        {formatCurrency(tax)}
      </TableCell>
      <TableCell style={{ textAlign: "right" }} className={classes.text}>
        {formatCurrency(lineTotal)}
      </TableCell>
    </TableRow>
  );
};

export const Ledger = ({ record }) => {
  const classes = useStyles();
  const items = record.lineItems?.items || [];
  const payments = (record.payments || []).filter(Boolean);
  if (!items.length) return null;

  return (
    <Table id="ledger" key="ledger" style={{ width: "100%" }}>
      <TableHead>
        <TableRow>
          <TableCell
            colSpan={2}
            style={{ textAlign: "left" }}
            className={classes.text}
          >
            <Typography variant="h5">Service</Typography>
          </TableCell>
          <TableCell style={{ textAlign: "right" }} className={classes.text}>
            <Typography variant="h5">Qty</Typography>
          </TableCell>
          <TableCell style={{ textAlign: "right" }} className={classes.text}>
            <Typography variant="h5">Rate</Typography>
          </TableCell>
          <TableCell style={{ textAlign: "left" }} className={classes.text}>
            <Typography variant="h5">Service Date</Typography>
          </TableCell>
          <TableCell style={{ textAlign: "right" }} className={classes.text}>
            <Typography variant="h5">Amount</Typography>
          </TableCell>
          <TableCell style={{ textAlign: "right" }} className={classes.text}>
            <Typography variant="h5">Tax</Typography>
          </TableCell>
          <TableCell style={{ textAlign: "right" }} className={classes.text}>
            <Typography variant="h5">Line Total</Typography>
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {items.map((item) => (
          <ServiceRow key={item.id || item.serviceId} item={item} />
        ))}

        {/* Blank spacer */}
        <TableRow>
          <TableCell colSpan={8} />
        </TableRow>

        <TableRow>
          <TableCell
            colSpan={7}
            style={{ textAlign: "right" }}
            className={classes.text}
          >
            <strong>Sub-total:</strong>
          </TableCell>
          <TableCell style={{ textAlign: "right" }} className={classes.text}>
            {formatCurrency(record.subtotal)}
          </TableCell>
        </TableRow>

        {record.discount && Number(record.discount) > 0 ? (
          <TableRow>
            <TableCell
              colSpan={7}
              style={{ textAlign: "right" }}
              className={classes.text}
            >
              <strong>Discount:</strong>
            </TableCell>
            <TableCell style={{ textAlign: "right" }} className={classes.text}>
              {formatCurrency(record.discount)}
            </TableCell>
          </TableRow>
        ) : null}

        {record.adjustment && Number(record.adjustment) > 0 ? (
          <TableRow>
            <TableCell
              colSpan={7}
              style={{ textAlign: "right" }}
              className={classes.text}
            >
              <strong>Adjustment:</strong>
            </TableCell>
            <TableCell style={{ textAlign: "right" }} className={classes.text}>
              {formatCurrency(record.adjustment)}
            </TableCell>
          </TableRow>
        ) : null}

        <TableRow>
          <TableCell
            colSpan={7}
            style={{ textAlign: "right" }}
            className={classes.text}
          >
            <strong>Taxes:</strong>
          </TableCell>
          <TableCell style={{ textAlign: "right" }} className={classes.text}>
            {formatCurrency(record.tax)}
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell
            colSpan={7}
            style={{ textAlign: "right" }}
            className={classes.text}
          >
            <strong>Total:</strong>
          </TableCell>
          <TableCell style={{ textAlign: "right" }} className={classes.text}>
            {formatCurrency(record.total)}
          </TableCell>
        </TableRow>

        {payments.length > 0 ? (
          <>
            {payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell
                  colSpan={7}
                  style={{ textAlign: "right" }}
                  className={classes.text}
                >
                  Payment{" "}
                  {payment.paymentDate ? formatDate(payment.paymentDate) : ""}
                  {describePaymentMethod(payment)
                    ? ` · ${describePaymentMethod(payment)}`
                    : ""}
                  :
                </TableCell>
                <TableCell
                  style={{ textAlign: "right" }}
                  className={classes.text}
                >
                  -{formatCurrency(payment.amount)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell
                colSpan={7}
                style={{ textAlign: "right" }}
                className={classes.text}
              >
                <strong>Balance Due:</strong>
              </TableCell>
              <TableCell
                style={{ textAlign: "right" }}
                className={classes.text}
              >
                <strong>
                  {formatCurrency(
                    record.status === InvoiceStatus.PAID
                      ? 0
                      : getBalance(record.total, sumPayments(payments)),
                  )}
                </strong>
              </TableCell>
            </TableRow>
          </>
        ) : null}

        {(record.notes ?? "").trim() ? (
          <>
            <TableRow>
              <TableCell colSpan={8} />
            </TableRow>
            <TableRow>
              <TableCell colSpan={8} className={classes.text}>
                <strong>Notes:</strong> {record.notes}
              </TableCell>
            </TableRow>
          </>
        ) : null}
      </TableBody>
    </Table>
  );
};
